import { createContext, useCallback, useContext } from 'react';
import { useScreenState } from './screenState';

// El "lugar" de la persona en la app: lo que el botón Atrás y Adelante del navegador mueven y lo que dice la dirección.
// Son las pantallas, la versión, y dentro de cada una lo que se lee como una página (sección de Administración, vista de
// Clientes, update de Prerrequisitos, búsqueda de Updates). Lo elegido, tildado, escrito o abierto en un cartel no es un lugar.
// Los datos nunca viajan en el historial: al volver a un lugar se piden de nuevo al servidor.
export const DEFAULT_PLACE = { tab: 'dashboard', version: '', q: '', section: 'status', view: 'status', mode: 'clients', cv: undefined, update: '' };

const TAB_IDS = ['republish', 'dashboard', 'prerequisites', 'clients', 'audit', 'admin', 'help'];
// Qué campos del lugar van en la dirección según la pantalla (la versión va siempre). `cv` es la versión de Clientes, que
// sigue a la del resto de la app mientras no se elija otra ahí: sin definir sigue a la general, vacía es "Ninguna".
const FIELDS = { dashboard: ['q'], prerequisites: ['update'], admin: ['section'], clients: ['view', 'mode', 'cv'] };
const ALLOWED = { view: ['status', 'compare'], mode: ['clients', 'versions'] };

// La dirección de un lugar: `?tab=clients&version=14.0.0.0&view=compare`. Updates sin versión ni búsqueda es la dirección de entrada.
export const placeUrl = (place) => {
    const params = new URLSearchParams({ tab: place.tab });
    if (place.version) params.set('version', place.version);
    (FIELDS[place.tab] || []).forEach(field => {
        if (place[field] !== undefined && place[field] !== DEFAULT_PLACE[field]) params.set(field, place[field]);
    });
    const entry = place.tab === 'dashboard' && [...params.keys()].length === 1;
    return window.location.pathname + (entry ? '' : `?${params}`);
};

// El lugar que dice una dirección (sólo los campos que le tocan), o null si no es de la app: el link del MCP no trae `tab`.
export const parsePlace = (search) => {
    const params = new URLSearchParams(search);
    const tab = params.get('tab');
    if (!TAB_IDS.includes(tab)) return null;
    const place = { tab, version: params.get('version') || '' };
    (FIELDS[tab] || []).forEach(field => {
        const value = params.has(field) ? params.get(field) : DEFAULT_PLACE[field];
        place[field] = !ALLOWED[field] || value === undefined || ALLOWED[field].includes(value) ? value : DEFAULT_PLACE[field];
    });
    return place;
};

// Cambiar de versión suelta la de Clientes: vuelve a seguir a la general.
export const nextPlace = (place, patch) => ({ ...place, ...patch, ...('version' in patch && !('cv' in patch) && patch.version !== place.version && { cv: undefined }) });

export const NavContext = createContext(null);

// El lugar actual y cómo ir a otro: `go({ tab: 'clients' })`. Cada `go` que cambia la dirección suma una entrada al historial.
// Sin `App` alrededor (una pantalla suelta) el lugar es un estado común de la pantalla.
export function useNav() {
    const nav = useContext(NavContext);
    const [local, setLocal] = useScreenState('nav.place', DEFAULT_PLACE);
    const goLocal = useCallback((patch) => setLocal(place => nextPlace(place, patch)), [setLocal]);
    return nav || { place: local, go: goLocal };
}
